/**
 * Buff Browz — GA4 analytics helper.
 *
 * Loaded as a classic (non-module) script so `window.track` is available to every
 * custom element and inline handler in the theme. gtag.js itself is loaded alongside
 * this file in snippets/scripts.liquid.
 *
 * See docs/ga4-tracking-plan.md for the full event spec.
 *
 * Convention: GA4 *recommended* events (add_to_cart, select_item, search,
 * select_promotion) keep their standard names. Custom events carry no store prefix, as several stores share this theme.
 */
(function () {
  const DEBUG = /[?&]ga_debug=1\b/.test(window.location.search);
  /**
   * Fire a GA4 event via gtag. No-ops safely if gtag has not loaded.
   * Strips null/undefined params so we never send empty custom dimensions.
   * @param {string} name
   * @param {Record<string, unknown>} [params]
   */
  function track(name, params) {
    const clean = {};
    if (params) {
      for (const key in params) {
        const value = params[key];
        if (value !== null && value !== undefined && value !== '') clean[key] = value;
      }
    }
    
    if (DEBUG) console.log('[GA4]', name, clean);

    if (typeof window.gtag !== 'function') {
      if (DEBUG) console.warn('[GA4] gtag not ready, dropped:', name);
      return;
    }
    window.gtag('event', name, clean);
  }

  /**
   * Convert a Shopify price in cents (or a numeric string) to major units.
   * @param {number|string|null|undefined} cents
   * @returns {number|undefined}
   */
  function toMoney(cents) {
    if (cents === null || cents === undefined || cents === '') return undefined;
    const n = typeof cents === 'string' ? parseFloat(cents) : cents;
    if (Number.isNaN(n)) return undefined;
    return Math.round(n) / 100;
  }

  /** Trailing debounce keyed by an arbitrary string, used for the search event. */
  const _timers = {};
  function debounce(key, fn, wait) {
    if (_timers[key]) clearTimeout(_timers[key]);
    _timers[key] = setTimeout(() => {
      delete _timers[key];
      fn();
    }, wait);
  }

  /**
   * Declarative click router.
   *
   * Any element (or ancestor of the click target) carrying `data-ga-event` fires that
   * event. Parameters come from `data-ga-param-<name>` attributes, e.g.
   *   <a data-ga-event="navigation_click" data-ga-param-link-text="Brows > Shop All">
   * yields track('navigation_click', { link_text: 'Brows > Shop All' }).
   *
   * Uses capture so it still fires when the theme calls preventDefault/stopPropagation
   * on navigation clicks. Delegated on document so it survives morph/section re-render.
   */
  const PARAM_PREFIX = 'data-ga-param-';
  document.addEventListener(
    'click',
    (event) => {
      const target = event.target;
      if (!(target instanceof Element)) return;
      const el = target.closest('[data-ga-event]');
      if (!el) return;

      const params = {};
      for (const attr of el.attributes) {
        if (attr.name.startsWith(PARAM_PREFIX)) {
          const key = attr.name.slice(PARAM_PREFIX.length).replace(/-/g, '_');
          params[key] = attr.value;
        }
      }
      track(el.getAttribute('data-ga-event'), params);
    },
    true
  );

  /**
   * Hero slideshow CTA clicks → GA4 `select_promotion`.
   * Fires on a CTA link/button inside a hero slide, the `_slide` block of the
   * slideshow section, identified by its `.slide__image-container`. Other
   * components reuse <slideshow-slide> (product card galleries, the PDP media
   * gallery, carousels, the announcement bar), so matching the element alone
   * counted product and carousel clicks as promotions. Slot = 1-based slide position;
   * promotion_name = the slide's first content block text (usually its heading),
   * falling back to the CTA label. The slide-navigation click (on the slide itself)
   * is excluded — we only count actual CTA anchors/buttons.
   */
  document.addEventListener(
    'click',
    (event) => {
      const target = event.target;
      if (!(target instanceof Element)) return;
      const cta = target.closest('slideshow-slide a[href], slideshow-slide .button, slideshow-slide button');
      if (!cta) return;

      const slide = cta.closest('slideshow-slide');
      const slideshow = cta.closest('slideshow-component');
      if (!slide || !slideshow) return;
      if (!slide.querySelector(':scope > .slide__image-container')) return;

      const allSlides = Array.from(slideshow.querySelectorAll('slideshow-slide'));
      const slot = allSlides.indexOf(slide) + 1;

      const content = slide.querySelector('.group-block-content') || slide.querySelector('.slide__content');
      const firstBlock = content && content.querySelector(':scope > *:not(.button)');
      const heading = firstBlock && firstBlock.textContent ? firstBlock.textContent.trim() : '';
      const ctaText = cta.textContent ? cta.textContent.trim() : '';

      track('select_promotion', {
        creative_name: 'slideshow',
        promotion_name: (heading || ctaText).slice(0, 100),
        creative_slot: slot || undefined,
        cta_text: ctaText.slice(0, 100),
        page_path: window.location.pathname,
      });
    },
    true
  );

  /**
   * Resolve which surface an add-to-cart came from, for `item_list_name`.
   * Direct single-variant adds fire from an inline product-form nested in the surface,
   * so ancestry works. Multi-variant "Choose" adds fire from the shared quick-add modal;
   * quick-add.js stamps `data-ga-list` on that dialog so we can still attribute them.
   * @param {Element} el
   * @returns {string}
   */
  function surfaceFor(el) {
    if (el.closest('cart-upsells')) return 'cart_upsell';
    if (el.closest('product-recommendations')) return 'product_upsell';
    if (el.closest('frequently-bought')) return 'frequently_bought';
    const dialog = el.closest('quick-add-dialog');
    if (dialog) return dialog.getAttribute('data-ga-list') || 'quick_buy';
    if (el.closest('.quick-add')) return 'quick_buy';
    return 'product_page';
  }

  /**
   * Global add-to-cart tracking → GA4 `add_to_cart`.
   *
   * Every single-variant add dispatches a CartAddEvent (event name `cart:update`) with
   * `data.source === 'product-form-component'`. We read the just-added line out of the
   * cart resource carried on the event and tag the surface via `item_list_name`.
   * The frequently-bought bundle uses `source: 'frequently-bought'` and is tracked in
   * frequently-bought.js instead, so it's excluded here.
   */
  document.addEventListener('cart:update', (event) => {
    const detail = /** @type {any} */ (event).detail;
    const data = detail && detail.data;
    if (!data || data.didError) return;
    if (data.source !== 'product-form-component') return;

    const form = event.target;
    if (!(form instanceof Element)) return;

    const cart = detail.resource;
    const items = cart && cart.items;
    if (!Array.isArray(items)) return;

    const variantId = form.querySelector('input[name="id"]')?.value;
    const line = variantId
      ? items.find((i) => String(i.variant_id) === String(variantId))
      : items.find((i) => String(i.product_id) === String(data.productId));
    if (!line) return;

    const quantity = Number(data.itemCount) || line.quantity || 1;
    const price = toMoney(line.price);
    const list = surfaceFor(form);

    track('add_to_cart', {
      currency: cart.currency || window.BuffAnalytics.currency,
      value: price != null ? price * quantity : undefined,
      item_list_name: list,
      items: [
        {
          item_id: String(line.product_id),
          item_name: line.product_title || line.title,
          item_variant: line.variant_title || undefined,
          price,
          quantity,
          item_list_name: list,
        },
      ],
    });
  });

  /**
   * Declarative product click-through router.
   * Any element carrying `data-ga-select-item` fires an event with a single item built
   * from its `data-ga-item-*` / `data-ga-list` attributes. A bare attribute fires GA4
   * `select_item` (cart upsells); a value names a separate event instead, so placements
   * that aren't item lists don't get folded into `select_item`:
   *   data-ga-select-item="search_result_select"  predictive search results
   *   data-ga-select-item="search_recently_viewed_select"  recently viewed, before typing
   *   data-ga-select-item="search_featured_select"  empty-state collection, before typing
   *   data-ga-list        item_list_name (cart_upsell)
   *   data-ga-item-id     product id
   *   data-ga-item-name   product title
   *   data-ga-item-price  price in major units
   *   data-ga-index       0-based position (optional)
   *   data-ga-search-term originating query (optional)
   */
  document.addEventListener(
    'click',
    (event) => {
      const target = event.target;
      if (!(target instanceof Element)) return;
      const el = target.closest('[data-ga-select-item]');
      if (!el) return;

      const d = el.dataset;
      track(el.getAttribute('data-ga-select-item') || 'select_item', {
        item_list_name: d.gaList,
        search_term: d.gaSearchTerm || undefined,
        items: [
          {
            item_id: d.gaItemId,
            item_name: d.gaItemName,
            price: d.gaItemPrice ? Number(d.gaItemPrice) : undefined,
            index: d.gaIndex ? Number(d.gaIndex) : undefined,
            item_list_name: d.gaList,
          },
        ],
      });
    },
    true
  );

  /**
   * Product Page Upsell click-through → GA4 `select_item`.
   * A click on a recommended product card (not its quick-add button) inside a
   * <product-recommendations> block. Product data comes from the card's dataset.
   */
  document.addEventListener(
    'click',
    (event) => {
      const target = event.target;
      if (!(target instanceof Element)) return;
      if (!target.closest('product-recommendations')) return;
      // Ignore the quick-add affordance — that path is tracked as add_to_cart.
      if (target.closest('.quick-add')) return;

      const card = target.closest('product-card');
      if (!card || !target.closest('a[href]')) return;

      const price = card.getAttribute('data-product-price');
      track('select_item', {
        item_list_name: 'product_upsell',
        items: [
          {
            item_id: card.getAttribute('data-product-id') || undefined,
            item_name: card.getAttribute('data-product-title') || undefined,
            price: price ? Number(price) : undefined,
            item_list_name: 'product_upsell',
          },
        ],
      });
    },
    true
  );

  /**
   * Klaviyo app-embed forms (the `klaviyo-form` block) submit through Klaviyo's own
   * onsite script, which emits a global `klaviyoForms` event. Catch the submit here.
   */
  window.addEventListener('klaviyoForms', (event) => {
    const detail = /** @type {any} */ (event).detail;
    if (detail && detail.type === 'submit') {
      track('newsletter_signup', { location: 'footer', list_source: 'klaviyo_embed', form_id: detail.formId });
    }
  });

  /**
   * Filter bar → GA4 `filter_select`.
   * Delegated change listener scoped to the facets. Fires only when a checkbox/radio
   * is switched ON. Group name comes from the enclosing disclosure's `.facets__label`;
   * value from the input's aria-label (falls back to its value).
   */
  document.addEventListener('change', (event) => {
    const input = event.target;
    if (!(input instanceof HTMLInputElement)) return;
    if (input.type !== 'checkbox' && input.type !== 'radio') return;
    if (!input.checked) return;
    if (!input.closest('.facets, facets-form-component')) return;

    const group = input.closest('details, .facets__disclosure');
    const filterName = group?.querySelector('.facets__label')?.textContent?.trim();
    const filterValue = input.getAttribute('aria-label') || input.value;

    track('filter_select', {
      filter_name: filterName,
      filter_value: filterValue,
      collection_title: /** @type {any} */ (window).Theme?.template?.collection_title || undefined,
    });
  });

  /**
   * Standard mobile drawer (header-drawer.liquid) navigation fallback → `navigation_click`.
   * The tabbed drawer and desktop menu carry declarative data-ga-event attributes handled by
   * the generic router; this covers the non-tabbed drawer's deep 3-level markup, which isn't
   * individually annotated. Skips already-annotated links so nothing double-counts. Builds the
   * path from ancestor <details> summaries + the leaf label.
   */
  document.addEventListener(
    'click',
    (event) => {
      const target = event.target;
      if (!(target instanceof Element)) return;
      const link = target.closest('.menu-drawer a.menu-drawer__menu-item[href]');
      if (!link || link.hasAttribute('data-ga-event')) return;

      const parts = [];
      let node = link.parentElement;
      while (node) {
        if (node.tagName === 'DETAILS') {
          const label = node.querySelector(':scope > summary .menu-drawer__menu-item-text, :scope > summary');
          const text = label && label.textContent ? label.textContent.trim().replace(/\s+/g, ' ') : '';
          if (text) parts.unshift(text);
        }
        node = node.parentElement;
      }
      const leafEl = link.querySelector('.menu-drawer__menu-item-text') || link;
      parts.push((leafEl.textContent || '').trim().replace(/\s+/g, ' '));

      track('navigation_click', {
        link_text: parts.filter(Boolean).join(' > '),
        link_url: link.getAttribute('href'),
      });
    },
    true
  );

  window.track = track;
  window.BuffAnalytics = { track, toMoney, debounce, currency: window.Shopify?.currency?.active || 'GBP' };
})();
